/**
 * Admin Dashboard - Platform Overview
 * Redesigned with tabs, sparklines, system health, and quick actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowsClockwise,
  BookOpen,
  Buildings,
  ChatText,
  CheckCircle,
  Clock,
  CurrencyDollar,
  Database,
  Envelope,
  HardDrives,
  Lightning,
  Microphone,
  Plus,
  Pulse,
  Robot,
  Sparkle,
  TrendDown,
  TrendUp,
  UsersThree,
  Warning,
  XCircle
} from '@phosphor-icons/react';
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('platform'); // platform, content
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState({
    api: 'healthy',
    database: 'healthy',
    email: 'healthy',
    webhooks: 'healthy'
  });

  useEffect(() => {
    fetchStats();
    checkSystemHealth();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch platform and content stats
      const platformData = await adminApiGet('/api/admin/stats/platform');
      const contentData = await adminApiGet('/api/admin/stats/content');
      
      // Transform to match component structure
      setStats({
        platform: {
          totalOrgs: platformData.total_organizations,
          totalUsers: platformData.total_users,
          activeUsers24h: platformData.active_users_24h,
          totalAgents: platformData.total_agents,
          voiceMinutes30d: platformData.voice_minutes_30d,
          llmTokens30d: platformData.llm_tokens_30d,
          newOrgs7d: platformData.new_orgs_7d,
          newOrgs30d: platformData.new_orgs_30d,
          trends: platformData.trends
        },
        content: {
          totalBlogs: contentData.total_blogs,
          publishedBlogs: contentData.published_blogs,
          totalEbooks: contentData.total_ebooks,
          downloads30d: contentData.downloads_30d,
          messages30d: contentData.messages_30d,
          unresolvedMessages: contentData.unresolved_messages
        }
      });
      
      // Set system health
      setSystemHealth(platformData.system_health);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };

  const checkSystemHealth = async () => {
    try {
      // TODO: Implement actual health checks
      // For now, simulate
      setSystemHealth({
        api: 'healthy',
        database: 'healthy',
        email: 'warning',
        webhooks: 'healthy'
      });
    } catch (error) {
      console.error('Error checking system health:', error);
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'warning':
        return <Warning size={16} className="text-yellow-500" />;
      case 'error':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Pulse size={16} className="text-gray-500" />;
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) {
      return <TrendUp size={14} className="text-green-500" />;
    } else if (trend < 0) {
      return <TrendDown size={14} className="text-red-500" />;
    }
    return null;
  };

  const StatCard = ({ icon: Icon, label, value, trend, color, loading }) => (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-[#6366F1]/30 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon size={24} className="text-white" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            trend > 0 ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
          }`}>
            {getTrendIcon(trend)}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-gray-400 text-sm mb-1">{label}</p>
      {loading ? (
        <div className="h-8 bg-white/10 rounded animate-pulse"></div>
      ) : (
        <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
      )}
    </div>
  );

  const QuickAction = ({ icon: Icon, label, onClick, variant = 'default' }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
        variant === 'primary'
          ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] border-transparent text-white hover:opacity-90'
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-[#6366F1]/30'
      }`}
    >
      <Icon size={18} />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-40 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
          <p className="text-gray-400">Platform overview and system health</p>
        </div>
        <button
          onClick={fetchStats}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
        >
          <ArrowsClockwise size={16} />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        <button
          onClick={() => setActiveTab('platform')}
          className={`px-4 py-2 text-sm font-medium transition-all ${
            activeTab === 'platform'
              ? 'text-[#6366F1] border-b-2 border-[#6366F1]'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Platform Stats
        </button>
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 text-sm font-medium transition-all ${
            activeTab === 'content'
              ? 'text-[#6366F1] border-b-2 border-[#6366F1]'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Content Stats
        </button>
      </div>

      {/* Platform Stats Tab */}
      {activeTab === 'platform' && stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Buildings}
              label="Total Organizations"
              value={stats.platform.totalOrgs}
              trend={stats.platform.trends.orgs}
              color="from-blue-500 to-blue-600"
            />
            <StatCard
              icon={UsersThree}
              label="Total Users"
              value={stats.platform.totalUsers}
              trend={stats.platform.trends.users}
              color="from-green-500 to-green-600"
            />
            <StatCard
              icon={Robot}
              label="Total AI Agents"
              value={stats.platform.totalAgents}
              trend={stats.platform.trends.agents}
              color="from-purple-500 to-purple-600"
            />
            <StatCard
              icon={Microphone}
              label="Voice Minutes (30d)"
              value={stats.platform.voiceMinutes30d}
              trend={stats.platform.trends.voice}
              color="from-pink-500 to-pink-600"
            />
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Pulse size={20} className="text-green-500" />
                <h3 className="text-white font-semibold">Active UsersThree (24h)</h3>
              </div>
              <p className="text-4xl font-bold text-white mb-2">{stats.platform.activeUsers24h}</p>
              <p className="text-sm text-gray-400">Currently online and active</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Lightning size={20} className="text-yellow-500" />
                <h3 className="text-white font-semibold">LLM Tokens (30d)</h3>
              </div>
              <p className="text-4xl font-bold text-white mb-2">{(stats.platform.llmTokens30d / 1000000).toFixed(1)}M</p>
              <p className="text-sm text-gray-400">Total tokens consumed</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendUp size={20} className="text-blue-500" />
                <h3 className="text-white font-semibold">New Orgs</h3>
              </div>
              <div className="flex items-baseline gap-4">
                <div>
                  <p className="text-2xl font-bold text-white">{stats.platform.newOrgs7d}</p>
                  <p className="text-xs text-gray-400">Last 7 days</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.platform.newOrgs30d}</p>
                  <p className="text-xs text-gray-400">Last 30 days</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
              <HardDrives size={20} />
              System Health
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg">
                {getHealthIcon(systemHealth.api)}
                <div>
                  <p className="text-sm font-medium text-white">API Status</p>
                  <p className="text-xs text-gray-400 capitalize">{systemHealth.api}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg">
                {getHealthIcon(systemHealth.database)}
                <div>
                  <p className="text-sm font-medium text-white">Database</p>
                  <p className="text-xs text-gray-400 capitalize">{systemHealth.database}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg">
                {getHealthIcon(systemHealth.email)}
                <div>
                  <p className="text-sm font-medium text-white">Email System</p>
                  <p className="text-xs text-gray-400 capitalize">{systemHealth.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-lg">
                {getHealthIcon(systemHealth.webhooks)}
                <div>
                  <p className="text-sm font-medium text-white">Webhooks</p>
                  <p className="text-xs text-gray-400 capitalize">{systemHealth.webhooks}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Content Stats Tab */}
      {activeTab === 'content' && stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard
              icon={BookOpen}
              label="Total Blogs"
              value={stats.content.totalBlogs}
              color="from-blue-500 to-blue-600"
            />
            <StatCard
              icon={Sparkle}
              label="Total Ebooks"
              value={stats.content.totalEbooks}
              color="from-purple-500 to-purple-600"
            />
            <StatCard
              icon={ChatText}
              label="Messages (30d)"
              value={stats.content.messages30d}
              color="from-green-500 to-green-600"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Content Overview</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Published Blogs</span>
                  <span className="text-white font-semibold">{stats.content.publishedBlogs}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Draft Blogs</span>
                  <span className="text-white font-semibold">{stats.content.totalBlogs - stats.content.publishedBlogs}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Ebook Downloads (30d)</span>
                  <span className="text-white font-semibold">{stats.content.downloads30d}</span>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">ChatText Queue</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total Messages</span>
                  <span className="text-white font-semibold">{stats.content.messages30d}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Unresolved</span>
                  <span className="text-yellow-500 font-semibold">{stats.content.unresolvedMessages}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold text-lg mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            icon={Plus}
            label="Create Blog"
            onClick={() => navigate('/admin/blogs/new')}
            variant="primary"
          />
          <QuickAction
            icon={Sparkle}
            label="Upload Ebook"
            onClick={() => navigate('/admin/ebooks/new')}
          />
          <QuickAction
            icon={Database}
            label="Trigger Backup"
            onClick={() => navigate('/admin/backups')}
          />
          <QuickAction
            icon={Warning}
            label="View Logs"
            onClick={() => navigate('/admin/logs')}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
